import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Button,
  TextField,
  InputAdornment,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  CircularProgress,
  Tooltip,
  Grid,
  Avatar,
  Link,
} from '@mui/material';
import {
  Search,
  Add,
  Edit,
  Delete,
  Store,
  Refresh,
  LocationOn,
  Phone,
  Image as ImageIcon,
  AccessTime,
  LocalOffer,
} from '@mui/icons-material';
import { getShops, createShop, updateShop, deleteShop } from '../services/shops';
import ShopFormDialog from './ShopFormDialog';
import ShopImagesDialog from './ShopImagesDialog';
import ShopHoursDialog from './ShopHoursDialog';
import ShopOfferFormDialog from './ShopOfferFormDialog';

const Shops = () => {
  const [shops, setShops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalShops, setTotalShops] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [imagesDialogOpen, setImagesDialogOpen] = useState(false);
  const [hoursDialogOpen, setHoursDialogOpen] = useState(false);
  const [offerDialogOpen, setOfferDialogOpen] = useState(false);
  const [selectedShop, setSelectedShop] = useState(null);
  const [isEdit, setIsEdit] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Load shops on mount and when page, rowsPerPage, or search changes
  useEffect(() => {
    fetchShops();
  }, [page, rowsPerPage, searchTerm]);

  const fetchShops = async () => {
    setLoading(true);
    try {
      const response = await getShops(page + 1, rowsPerPage, searchTerm);
      setShops(response.shops || []);
      setTotalShops(response.total || 0);
    } catch (err) {
      setError('Failed to fetch shops');
      setShops([]);
      setTotalShops(0);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setPage(0);
  };

  const handleAddShop = () => {
    setSelectedShop(null);
    setIsEdit(false);
    setFormDialogOpen(true);
  };

  const handleEditShop = (shop) => {
    setSelectedShop(shop);
    setIsEdit(true);
    setFormDialogOpen(true);
  };

  const handleDeleteClick = (shop) => {
    setSelectedShop(shop);
    setDeleteDialogOpen(true);
  };

  const handleImagesClick = (shop) => {
    setSelectedShop(shop);
    setImagesDialogOpen(true);
  };

  const handleHoursClick = (shop) => {
    setSelectedShop(shop);
    setHoursDialogOpen(true);
  };

  const handleOfferClick = (shop) => {
    setSelectedShop(shop);
    setOfferDialogOpen(true);
  };

  const handleFormSubmit = async (formData) => {
    try {
      if (isEdit) {
        await updateShop(selectedShop.id, formData);
        setSuccess('Shop updated successfully!');
      } else {
        await createShop(formData);
        setSuccess('Shop created successfully!');
      }
      setFormDialogOpen(false);
      fetchShops();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(isEdit ? 'Failed to update shop' : 'Failed to create shop');
      setTimeout(() => setError(''), 3000);
    }
  };

  const handleDeleteConfirm = async () => {
    try {
      await deleteShop(selectedShop.id);
      setSuccess('Shop deleted successfully!');
      setDeleteDialogOpen(false);
      fetchShops();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to delete shop');
      setTimeout(() => setError(''), 3000);
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      {/* Header Section */}
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h4"
          component="h1"
          sx={{
            fontWeight: 700,
            background: 'linear-gradient(135deg, #52ad83 0%, #3d8f64 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            mb: 1,
          }}
        >
          Shops Management
        </Typography>
        <Typography variant="body1" sx={{ color: '#6b7280' }}>
          Manage shop listings, owners, and details
        </Typography>
      </Box>


      {/* Action Bar */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          borderRadius: 3,
          background: 'linear-gradient(145deg, #ffffff 0%, #f3f4f6 100%)',
          border: '1px solid rgba(0,0,0,0.08)',
          boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
        }}
      >
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              placeholder="Search shops..."
              value={searchTerm}
              onChange={handleSearch}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ color: '#9ca3af' }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  background: 'rgba(0,0,0,0.02)',
                  '&:hover fieldset': {
                    borderColor: '#52ad83',
                  },
                },
              }}
            />
          </Grid>
          <Grid item xs={12} md={6} sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Tooltip title="Refresh">
              <IconButton
                onClick={fetchShops}
                sx={{
                  color: '#6b7280',
                  border: '1px solid rgba(0,0,0,0.1)',
                  borderRadius: 2,
                  '&:hover': { color: '#52ad83', borderColor: '#52ad83' },
                }}
              >
                <Refresh />
              </IconButton>
            </Tooltip>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={handleAddShop}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: 2,
                background: 'linear-gradient(135deg, #52ad83 0%, #3d8f64 100%)',
                boxShadow: '0 4px 15px rgba(82, 173, 131, 0.3)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #3d8f64 0%, #52ad83 100%)',
                  boxShadow: '0 6px 20px rgba(82, 173, 131, 0.4)',
                },
              }}
            >
              Add New Shop
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Success/Error Messages */}
      {success && (
        <Paper
          elevation={0}
          sx={{
            p: 2,
            mb: 3,
            borderRadius: 2,
            background: 'rgba(34, 197, 94, 0.08)',
            border: '1px solid rgba(34, 197, 94, 0.2)',
          }}
        >
          <Typography sx={{ color: '#16a34a' }}>{success}</Typography>
        </Paper>
      )}
      {error && (
        <Paper
          elevation={0}
          sx={{
            p: 2,
            mb: 3,
            borderRadius: 2,
            background: 'rgba(239, 68, 68, 0.08)',
            border: '1px solid rgba(239, 68, 68, 0.2)',
          }}
        >
          <Typography sx={{ color: '#dc2626' }}>{error}</Typography>
        </Paper>
      )}

      {/* Shops Table */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: 3,
          background: 'linear-gradient(145deg, #ffffff 0%, #f3f4f6 100%)',
          border: '1px solid rgba(0,0,0,0.08)',
          boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
          overflow: 'hidden',
        }}
      >
        {loading ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              p: 6,
            }}
          >
            <CircularProgress sx={{ color: '#52ad83' }} />
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow
                    sx={{
                      background: 'rgba(79, 70, 229, 0.05)',
                      '& th': {
                        color: '#374151',
                        fontWeight: 600,
                        fontSize: '0.875rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        py: 2,
                        borderBottom: '1px solid rgba(0,0,0,0.08)',
                      },
                    }}
                  >
                    <TableCell>ID</TableCell>
                    <TableCell>Shop</TableCell>
                    <TableCell>Category</TableCell>
                    <TableCell>Contact</TableCell>
                    <TableCell>Location</TableCell>
                    <TableCell>Images</TableCell>
                    <TableCell>Featured</TableCell>
                    <TableCell>Test Shop</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell align="center">Manage</TableCell>
                    <TableCell align="center">Delete</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {shops.length === 0 ? (
                    <TableRow>
                                                <TableCell colSpan={10} align="center" sx={{ py: 6 }}>
                                              <Box sx={{ textAlign: 'center' }}>
                                                <Store sx={{ fontSize: 48, color: '#9ca3af', mb: 2 }} />
                                                <Typography variant="h6" sx={{ color: '#374151', mb: 1 }}>
                                                  No shops found
                                                </Typography>
                                                <Typography variant="body2" sx={{ color: '#6b7280' }}>
                                                  {searchTerm
                                                    ? 'Try adjusting your search criteria'
                                                    : 'Get started by adding a new shop'}
                                                </Typography>
                                              </Box>
                                            </TableCell>                    </TableRow>
                  ) : (
                    shops.map((shop, index) => (
                        <TableRow
                          key={shop.id}
                          sx={{
                            '&:hover': {
                              background: 'rgba(0,0,0,0.02)',
                            },
                            '& td': {
                              borderBottom: '1px solid rgba(0,0,0,0.05)',
                              py: 2,
                              color: '#374151',
                            },
                          }}
                        >
                          <TableCell>
                            <Typography variant="body2" sx={{ color: '#6b7280' }}>
                              #{shop.id || index + 1}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                              <Avatar
                                src={shop.ShopImages?.find(img => img.type === 'logo')?.imageUrl}
                                sx={{
                                  width: 40,
                                  height: 40,
                                  borderRadius: 2,
                                  background:
                                    'linear-gradient(135deg, #52ad83 0%, #3d8f64 100%)',
                                }}
                              >
                                <Store sx={{ fontSize: 20, color: 'white' }} />
                              </Avatar>
                              <Box>
                                <Typography variant="body2" fontWeight={500}>
                                  {shop.name}
                                </Typography>
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={shop.CategoryType?.name}
                              size="small"
                              sx={{
                                background: 'rgba(79, 70, 229, 0.1)',
                                color: '#4f46e5',
                                fontWeight: 600,
                                fontSize: '0.75rem',
                                borderRadius: 1.5,
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Phone sx={{ fontSize: 18, color: '#6b7280' }} />
                              <Typography variant="body2">{shop.contactNumber}</Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <LocationOn sx={{ fontSize: 16, color: '#6b7280' }} />
                              {shop.latitude && shop.longitude ? (
                                <Link
                                  href={`https://www.google.com/maps?q=${shop.latitude},${shop.longitude}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  sx={{ color: '#4f46e5', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                                >
                                  <Typography variant="body2">{shop.location}</Typography>
                                </Link>
                              ) : (
                                <Typography variant="body2">{shop.location}</Typography>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ color: '#6b7280' }}>
                              {shop.ShopImages?.length}
                            </Typography>
                          </TableCell>
                          <TableCell>
                          <Chip
                              label={shop.is_featured ? "Yes" : "No"}
                              size="small"
                              sx={{
                                background: shop.is_featured ? 'rgba(79, 70, 229, 0.1)' : 'rgba(0,0,0,0.05)',
                                color: shop.is_featured ? '#4f46e5' : '#6b7280',
                                fontWeight: 600,
                                fontSize: '0.75rem',
                                borderRadius: 1.5,
                              }}
                            />
                          </TableCell>
                          <TableCell>
                          <Chip
                              label={shop.is_test_shop ? "Yes" : "No"}
                              size="small"
                              sx={{
                                background: shop.is_test_shop ? 'rgba(245, 158, 11, 0.1)' : 'rgba(0,0,0,0.05)',
                                color: shop.is_test_shop ? '#f59e0b' : '#6b7280',
                                fontWeight: 600,
                                fontSize: '0.75rem',
                                borderRadius: 1.5,
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ color: '#6b7280' }}>
                              {shop.createdAt
                                ? new Date(shop.createdAt).toLocaleDateString()
                                : '-'}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                                <Tooltip title="Update Hours">
                                    <IconButton
                                    size="small"
                                    onClick={() => handleHoursClick(shop)}
                                    sx={{
                                        color: '#6b7280',
                                        '&:hover': { 
                                        color: '#ff9800',
                                        background: 'rgba(255, 152, 0, 0.1)',
                                        },
                                    }}
                                    >
                                    <AccessTime fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Manage Offers">
                                    <IconButton
                                    size="small"
                                    onClick={() => handleOfferClick(shop)}
                                    sx={{
                                        color: '#6b7280',
                                        '&:hover': { 
                                        color: '#ff9800',
                                        background: 'rgba(255, 152, 0, 0.1)',
                                        },
                                    }}
                                    >
                                    <LocalOffer fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Manage Images">
                                    <IconButton
                                    size="small"
                                    onClick={() => handleImagesClick(shop)}
                                    sx={{
                                        color: '#6b7280',
                                        '&:hover': { 
                                        color: '#52ad83',
                                        background: 'rgba(82, 173, 131, 0.1)',
                                        },
                                    }}
                                    >
                                    <ImageIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Edit">
                                    <IconButton
                                    size="small"
                                    onClick={() => handleEditShop(shop)}
                                    sx={{
                                        color: '#6b7280',
                                        '&:hover': { 
                                        color: '#4f46e5',
                                        background: 'rgba(79, 70, 229, 0.1)',
                                        },
                                    }}
                                    >
                                    <Edit fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                          </TableCell>
                          <TableCell align="center">
                            <Tooltip title="Delete">
                              <IconButton
                                size="small"
                                onClick={() => handleDeleteClick(shop)}
                                sx={{
                                  color: '#dc2626',
                                  '&:hover': { 
                                    color: '#b91c1c',
                                    background: 'rgba(220, 38, 38, 0.1)',
                                   },
                                }}
                              >
                                <Delete fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={totalShops}
              page={page}
              onPageChange={(e, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[5, 10, 25]}
              sx={{
                color: '#6b7280',
                borderTop: '1px solid rgba(0,0,0,0.08)',
                '& .MuiTablePagination-select': {
                  color: '#374151',
                },
                '& .MuiTablePagination-selectIcon': {
                  color: '#6b7280',
                },
                '& .MuiTablePagination-actions button': {
                  color: '#6b7280',
                  '&:hover': {
                    color: '#4f46e5',
                    background: 'rgba(79, 70, 229, 0.08)',
                  },
                },
              }}
            />
          </>
        )}
      </Paper>

      {/* Add/Edit Shop Dialog */}
      <ShopFormDialog
        open={formDialogOpen}
        onClose={() => setFormDialogOpen(false)}
        onSubmit={handleFormSubmit}
        shop={selectedShop}
        isEdit={isEdit}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: 3,
            background: 'linear-gradient(145deg, #ffffff 0%, #f3f4f6 100%)',
            border: '1px solid rgba(0,0,0,0.08)',
            maxWidth: 400,
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            color: '#dc2626',
          }}
        >
          <Delete />
          Delete Shop
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: '#6b7280' }}>
            Are you sure you want to delete <strong>{selectedShop?.name}</strong>?
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 1 }}>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            sx={{
              textTransform: 'none',
              color: '#6b7280',
              '&:hover': { color: '#374151', background: 'rgba(0,0,0,0.04)' },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: 2,
              background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
              boxShadow: '0 4px 15px rgba(220, 38, 38, 0.3)',
              '&:hover': {
                background: 'linear-gradient(135deg, #b91c1c 0%, #dc2626 100%)',
                boxShadow: '0 6px 20px rgba(220, 38, 38, 0.4)',
              },
            }}
          >
            Delete Shop
          </Button>
        </DialogActions>
      </Dialog>

      {/* Manage Shop Images Dialog */}
            <ShopImagesDialog
        open={imagesDialogOpen}
        onClose={() => setImagesDialogOpen(false)}
        shopId={selectedShop?.id}
      />

      {/* Manage Shop Hours Dialog */}
      <ShopHoursDialog
        open={hoursDialogOpen}
        onClose={() => setHoursDialogOpen(false)}
        shopId={selectedShop?.id}
      />
      
      {/* Manage Shop Offer Dialog */}
      <ShopOfferFormDialog
        open={offerDialogOpen}
        onClose={() => setOfferDialogOpen(false)}
        shopId={selectedShop?.id}
      />
    </Box>
  );
};

export default Shops;
